// @tkos/demo-engine — a self-running, scripted demo overlay.
//
// Pure front-end SIMULATION: it renders a floating control bar + narration bar
// and plays a script of steps over the real portal UI. Steps render *simulated*
// cards/messages/highlights — the engine never calls a mutating API, never
// touches payments/Stripe/real data. Reset = restart playback. Used by both the
// retailer and customer apps with per-app step scripts.

export interface DemoApi {
  /** Pulse-highlight a target element (by selector or element). Spotlights it. */
  highlight(target: string | Element | null, opts?: { label?: string }): void;
  clearHighlight(): void;
  /** A simulated Kit/alert card (top-centre). Returns the element. */
  showAlertCard(opts: { icon?: string; title: string; detail?: string; tone?: "info" | "success" | "warn" }): HTMLElement;
  /** A simulated chat/message bubble (e.g. "message sent to Sarah"). */
  showMessage(opts: { from?: string; text: string; side?: "in" | "out" }): HTMLElement;
  /** A generic simulated floating card (raw HTML body). */
  showCard(html: string, opts?: { wide?: boolean }): HTMLElement;
  /** Simulate typing `text` into a target input (visual only). */
  typeInto(target: string | Element | null, text: string): Promise<void>;
  /** A simulated toast. */
  toast(text: string): void;
  /** Best-effort: add `.is-open` to a panel selector (cosmetic open). */
  openSelector(selector: string): void;
  /** Remove all simulated overlays this step created (called between steps). */
  clearOverlays(): void;
  /** Sleep, scaled by the current playback speed. */
  wait(ms: number): Promise<void>;
  /** True once the run has been stopped/aborted — long steps should bail. */
  readonly aborted: boolean;
}

export interface DemoStep {
  name: string;
  narration: string;
  /** Base dwell after the step's actions, at 1× (default 3500ms). */
  durationMs?: number;
  run?: (api: DemoApi) => void | Promise<void>;
}

export interface DemoConfig {
  title: string;            // e.g. "Retailer Demo — A Day in the Life"
  steps: DemoStep[];
  accent?: string;          // brand accent (defaults to amber)
}

export interface DemoHandle { stop(): void; }

const STYLE_ID = "tkos-demo-style";
const ROOT_ID = "tkos-demo-root";

function injectStyle(accent: string): void {
  let s = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!s) { s = document.createElement("style"); s.id = STYLE_ID; document.head.appendChild(s); }
  s.textContent = `
    #${ROOT_ID}{position:fixed;inset:0;z-index:2147483600;pointer-events:none;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif}
    #${ROOT_ID} *{box-sizing:border-box}
    .tkd-bar{position:fixed;top:12px;left:50%;transform:translateX(-50%);display:flex;align-items:center;gap:10px;
      pointer-events:auto;background:#16181d;color:#fff;border:1px solid rgba(255,255,255,.12);border-radius:12px;
      padding:8px 10px;box-shadow:0 12px 36px rgba(0,0,0,.5);max-width:calc(100vw - 24px)}
    .tkd-badge{background:${accent};color:#1a1206;font-weight:800;font-size:.62rem;letter-spacing:.08em;border-radius:6px;padding:.2rem .45rem;text-transform:uppercase;flex:none}
    .tkd-btn{background:transparent;border:0;color:#fff;cursor:pointer;font-size:1rem;line-height:1;padding:.2rem .35rem;border-radius:6px;flex:none}
    .tkd-btn:hover{background:rgba(255,255,255,.12)}
    .tkd-btn:disabled{opacity:.4;cursor:default}
    .tkd-step{font-size:.78rem;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:32vw}
    .tkd-step b{color:${accent}}
    .tkd-sep{width:1px;height:18px;background:rgba(255,255,255,.15);flex:none}
    .tkd-speed{background:rgba(255,255,255,.1);border:0;color:#fff;border-radius:6px;font-size:.72rem;font-weight:700;padding:.25rem .4rem;cursor:pointer;flex:none}
    .tkd-exit{background:rgba(239,68,68,.2);color:#fca5a5;border:1px solid rgba(239,68,68,.35);border-radius:6px;font-size:.72rem;font-weight:700;padding:.25rem .55rem;cursor:pointer;flex:none}
    .tkd-narr{position:fixed;top:60px;left:50%;transform:translateX(-50%);pointer-events:none;
      background:rgba(22,24,29,.92);color:#f3f4f6;border:1px solid rgba(255,255,255,.1);border-radius:10px;
      padding:.5rem .9rem;font-size:.84rem;line-height:1.4;max-width:min(560px,calc(100vw - 24px));text-align:center;
      box-shadow:0 8px 24px rgba(0,0,0,.4);opacity:0;transition:opacity .25s ease}
    .tkd-narr.show{opacity:1}
    .tkd-ring{position:fixed;border:2px solid ${accent};border-radius:12px;pointer-events:none;
      box-shadow:0 0 0 3px ${accent}55, 0 0 0 9999px rgba(8,9,12,.2);transition:all .3s ease;animation:tkdPulse 1.4s ease-in-out infinite}
    @keyframes tkdPulse{0%,100%{box-shadow:0 0 0 3px ${accent}55,0 0 0 9999px rgba(8,9,12,.2)}50%{box-shadow:0 0 0 7px ${accent}33,0 0 0 9999px rgba(8,9,12,.2)}}
    .tkd-ring-label{position:absolute;top:-26px;left:0;background:${accent};color:#1a1206;font-size:.66rem;font-weight:800;border-radius:6px;padding:.12rem .4rem;white-space:nowrap}
    .tkd-stack{position:fixed;top:108px;left:50%;transform:translateX(-50%);display:flex;flex-direction:column;gap:10px;align-items:center;pointer-events:none;width:min(420px,calc(100vw - 24px))}
    .tkd-card{width:100%;background:#fff;color:#16181d;border-radius:12px;border:1px solid rgba(0,0,0,.1);box-shadow:0 16px 40px rgba(0,0,0,.3);padding:12px 14px;animation:tkdIn .3s ease}
    .tkd-card.wide{width:min(560px,calc(100vw - 24px))}
    @keyframes tkdIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
    .tkd-alert{display:flex;gap:10px;align-items:flex-start;border-left:4px solid ${accent}}
    .tkd-alert.success{border-left-color:#22c55e} .tkd-alert.warn{border-left-color:#f59e0b}
    .tkd-alert__ico{flex:none;width:30px;height:30px;border-radius:8px;background:${accent}22;display:flex;align-items:center;justify-content:center;font-size:1rem}
    .tkd-alert__t{font-weight:800;font-size:.9rem} .tkd-alert__d{color:#6b7280;font-size:.8rem;margin-top:2px}
    .tkd-msg{display:flex} .tkd-msg.out{justify-content:flex-end}
    .tkd-msg__b{max-width:80%;padding:8px 12px;border-radius:14px;font-size:.84rem;line-height:1.4}
    .tkd-msg.out .tkd-msg__b{background:${accent};color:#1a1206;border-bottom-right-radius:4px}
    .tkd-msg.in .tkd-msg__b{background:#fff;color:#16181d;border:1px solid rgba(0,0,0,.1);border-bottom-left-radius:4px}
    .tkd-msg__from{font-size:.66rem;color:#9ca3af;margin-bottom:2px}
    .tkd-toast{position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#16181d;color:#fff;border-radius:10px;padding:.5rem .9rem;font-size:.82rem;box-shadow:0 10px 30px rgba(0,0,0,.4);animation:tkdIn .25s ease;pointer-events:none}
  `;
}

export function startDemo(config: DemoConfig): DemoHandle {
  const accent = config.accent || "#E8A020";
  injectStyle(accent);
  document.getElementById(ROOT_ID)?.remove();

  const root = document.createElement("div");
  root.id = ROOT_ID;
  const total = config.steps.length;
  root.innerHTML = `
    <div class="tkd-bar" role="region" aria-label="Demo controls">
      <span class="tkd-badge">Demo Mode</span>
      <button class="tkd-btn" data-act="prev" title="Previous" aria-label="Previous">⏮</button>
      <button class="tkd-btn" data-act="play" title="Play/Pause" aria-label="Play or pause">⏸</button>
      <button class="tkd-btn" data-act="next" title="Next" aria-label="Next">⏭</button>
      <span class="tkd-sep"></span>
      <span class="tkd-step" data-role="step"></span>
      <span class="tkd-sep"></span>
      <button class="tkd-speed" data-act="speed" title="Speed">1×</button>
      <button class="tkd-exit" data-act="exit">Exit</button>
    </div>
    <div class="tkd-narr" data-role="narr"></div>
    <div class="tkd-stack" data-role="stack"></div>`;
  document.body.appendChild(root);

  const bar = root.querySelector(".tkd-bar") as HTMLElement;
  const stepEl = root.querySelector("[data-role=step]") as HTMLElement;
  const narrEl = root.querySelector("[data-role=narr]") as HTMLElement;
  const stackEl = root.querySelector("[data-role=stack]") as HTMLElement;
  const playBtn = bar.querySelector("[data-act=play]") as HTMLButtonElement;
  const speedBtn = bar.querySelector("[data-act=speed]") as HTMLButtonElement;

  const SPEEDS = [1, 1.5, 2];
  let speedIdx = 0;
  let idx = 0;
  let playing = true;
  let aborted = false;
  let ring: HTMLElement | null = null;
  let resolveGate: (() => void) | null = null;

  const speed = () => SPEEDS[speedIdx]!;
  const sleep = (ms: number) => new Promise<void>((r) => window.setTimeout(r, Math.max(0, ms / speed())));
  // Pause-aware wait that also resolves on abort.
  function gatedWait(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      let done = false;
      const fin = () => { if (done) return; done = true; resolveGate = null; resolve(); };
      const tick = () => {
        if (aborted) return fin();
        if (!playing) { resolveGate = () => { resolveGate = null; void sleep(ms).then(fin); }; return; }
        void sleep(ms).then(fin);
      };
      tick();
    });
  }

  function clearOverlays(): void { stackEl.innerHTML = ""; }
  function clearHighlight(): void { ring?.remove(); ring = null; }

  const api: DemoApi = {
    get aborted() { return aborted; },
    wait: (ms) => sleep(ms),
    clearOverlays,
    clearHighlight,
    highlight(target, opts) {
      clearHighlight();
      const el = typeof target === "string" ? document.querySelector(target) : target;
      if (!el) return;
      const r = (el as Element).getBoundingClientRect();
      if (!r.width && !r.height) return;
      ring = document.createElement("div");
      ring.className = "tkd-ring";
      ring.style.cssText += `left:${r.left - 6}px;top:${r.top - 6}px;width:${r.width + 12}px;height:${r.height + 12}px;`;
      if (opts?.label) { const l = document.createElement("span"); l.className = "tkd-ring-label"; l.textContent = opts.label; ring.appendChild(l); }
      root.appendChild(ring);
      try { (el as Element).scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* ignore */ }
    },
    showAlertCard(o) {
      const card = document.createElement("div");
      card.className = "tkd-card";
      card.innerHTML = `<div class="tkd-alert ${o.tone || ""}"><div class="tkd-alert__ico">${o.icon || "🔔"}</div><div><div class="tkd-alert__t"></div><div class="tkd-alert__d"></div></div></div>`;
      (card.querySelector(".tkd-alert__t") as HTMLElement).textContent = o.title;
      (card.querySelector(".tkd-alert__d") as HTMLElement).textContent = o.detail || "";
      stackEl.appendChild(card);
      return card;
    },
    showMessage(o) {
      const wrap = document.createElement("div");
      wrap.className = `tkd-msg ${o.side === "out" ? "out" : "in"}`;
      const from = o.from ? `<div class="tkd-msg__from">${escapeHtml(o.from)}</div>` : "";
      wrap.innerHTML = `<div>${from}<div class="tkd-msg__b"></div></div>`;
      (wrap.querySelector(".tkd-msg__b") as HTMLElement).textContent = o.text;
      stackEl.appendChild(wrap);
      return wrap;
    },
    showCard(html, opts) {
      const card = document.createElement("div");
      card.className = "tkd-card" + (opts?.wide ? " wide" : "");
      card.innerHTML = html;
      stackEl.appendChild(card);
      return card;
    },
    async typeInto(target, text) {
      const el = typeof target === "string" ? document.querySelector(target) : target;
      if (!el) { api.showMessage({ side: "out", text }); return; }
      const input = el as HTMLInputElement | HTMLTextAreaElement;
      input.focus?.();
      input.value = "";
      for (let i = 0; i < text.length; i++) {
        if (aborted) return;
        input.value += text[i];
        input.dispatchEvent(new Event("input", { bubbles: true }));
        await sleep(28);
      }
    },
    toast(text) {
      const t = document.createElement("div");
      t.className = "tkd-toast";
      t.textContent = text;
      root.appendChild(t);
      window.setTimeout(() => t.remove(), 2600 / speed());
    },
    openSelector(sel) { document.querySelector(sel)?.classList.add("is-open"); },
  };

  function renderStep(): void {
    const s = config.steps[idx];
    if (!s) return;
    stepEl.innerHTML = `Step <b>${idx + 1}</b> of ${total} — ${escapeHtml(s.name)}`;
    narrEl.textContent = s.narration;
    narrEl.classList.add("show");
  }

  async function runFrom(start: number): Promise<void> {
    for (idx = start; idx < total && !aborted; idx++) {
      clearOverlays(); clearHighlight();
      renderStep();
      const s = config.steps[idx]!;
      try { if (s.run) await s.run(api); } catch { /* a step error shouldn't kill the demo */ }
      if (aborted) return;
      await gatedWait(s.durationMs ?? 3500);
    }
    if (!aborted) { playing = false; playBtn.textContent = "▶"; api.toast("Demo complete — press ▶ to replay"); idx = total - 1; }
  }

  function restartAt(n: number): void {
    aborted = true;                 // cancel current loop
    window.setTimeout(() => { aborted = false; void runFrom(Math.max(0, Math.min(total - 1, n))); }, 60);
  }

  bar.addEventListener("click", (e) => {
    const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset["act"];
    if (act === "play") {
      playing = !playing;
      playBtn.textContent = playing ? "⏸" : "▶";
      if (playing && resolveGate) resolveGate();
      else if (playing && idx >= total - 1) restartAt(0); // replay if finished
    } else if (act === "next") { restartAt(idx + 1); playing = true; playBtn.textContent = "⏸"; }
    else if (act === "prev") { restartAt(idx - 1); playing = true; playBtn.textContent = "⏸"; }
    else if (act === "speed") { speedIdx = (speedIdx + 1) % SPEEDS.length; speedBtn.textContent = `${speed()}×`; }
    else if (act === "exit") { handle.stop(); }
  });

  const handle: DemoHandle = {
    stop() {
      aborted = true;
      clearHighlight();
      root.remove();
      // Return the portal to normal: drop ?demo from the URL.
      try {
        const u = new URL(window.location.href);
        if (u.searchParams.has("demo")) { u.searchParams.delete("demo"); window.history.replaceState({}, "", u.toString()); }
      } catch { /* ignore */ }
    },
  };

  void runFrom(0);
  return handle;
}

function escapeHtml(s: string): string {
  return String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
}
